import React, { useEffect, useId, useRef, useState } from 'react';
import { FaCamera, FaImage, FaSpinner, FaTrash, FaUpload } from 'react-icons/fa';
import { compressImage, formatFileSize, looksLikeImage } from '../utils/imageCompress';

/**
 * Upload gambar dengan:
 * - kompresi otomatis
 * - pilihan Galeri/File atau Kamera (cocok untuk tablet/iPad)
 *
 * value: File | string (URL existing) | null
 */
function ImageUpload({
  label = 'Upload Foto',
  name,
  value = null,
  onChange,
  onClear,
  required = false,
  optional = false,
  className = '',
  previewSize = 'md', // sm | md
}) {
  const baseId = useId();
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [compressing, setCompressing] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [meta, setMeta] = useState(null); // { name, size, originalSize }

  useEffect(() => {
    let objectUrl = null;

    if (value instanceof File || value instanceof Blob) {
      objectUrl = URL.createObjectURL(value);
      setPreviewUrl(objectUrl);
      setMeta((prev) => ({
        name: value.name || 'foto.jpg',
        size: value.size,
        originalSize: prev?.originalSize ?? null,
      }));
    } else if (typeof value === 'string' && value) {
      setPreviewUrl(value);
      setMeta({ name: 'Foto tersimpan', size: null, originalSize: null });
    } else {
      setPreviewUrl(null);
      setMeta(null);
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [value]);

  const handlePick = async (event) => {
    const file = event.target.files && event.target.files[0];
    // reset supaya file yang sama bisa dipilih lagi
    event.target.value = '';

    if (!file) return;

    if (!looksLikeImage(file)) {
      setError('File harus berupa gambar');
      return;
    }

    setError('');
    setCompressing(true);
    const originalSize = file.size;

    try {
      const compressed = await compressImage(file);
      setMeta({
        name: compressed.name,
        size: compressed.size,
        originalSize,
      });
      onChange?.(compressed, name);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Gagal memproses gambar');
    } finally {
      setCompressing(false);
    }
  };

  const handleClear = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setError('');
    if (onClear) {
      onClear(name);
    } else {
      onChange?.(null, name);
    }
  };

  const previewClass =
    previewSize === 'sm' ? 'w-20 h-20' : 'w-28 h-28 sm:w-32 sm:h-32';

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500"> *</span>}
          {optional && (
            <span className="text-gray-400 font-normal"> (Opsional)</span>
          )}
        </label>
      )}

      <input
        ref={galleryInputRef}
        id={`${baseId}-gallery`}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePick}
        disabled={compressing}
      />
      <input
        ref={cameraInputRef}
        id={`${baseId}-camera`}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handlePick}
        disabled={compressing}
      />

      <div
        className={`border-2 border-dashed rounded-xl p-3 sm:p-4 transition-colors ${
          previewUrl
            ? 'border-green-300 bg-green-50'
            : 'border-gray-300 bg-gray-50/50'
        }`}
      >
        {previewUrl ? (
          <div className="flex flex-col items-center text-center gap-2">
            <img
              src={previewUrl}
              alt="Preview"
              className={`${previewClass} object-cover rounded-lg border-2 border-green-300`}
            />
            <div className="min-w-0 w-full">
              <p className="text-sm text-green-700 font-medium truncate px-2">
                {meta?.name}
              </p>
              {meta?.size != null && (
                <p className="text-xs text-green-600">
                  {meta.originalSize != null && meta.originalSize > meta.size
                    ? `${formatFileSize(meta.originalSize)} → ${formatFileSize(meta.size)} (dikompres)`
                    : `${formatFileSize(meta.size)} (sudah dikompres)`}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                disabled={compressing}
                className="text-xs px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Ganti dari Galeri
              </button>
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={compressing}
                className="text-xs px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Ambil Ulang
              </button>
              <button
                type="button"
                onClick={handleClear}
                disabled={compressing}
                className="text-xs px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1"
              >
                <FaTrash className="text-[10px]" />
                Hapus
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-2">
            <FaUpload className="mx-auto h-7 w-7 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600 mb-3">
              Pilih sumber gambar
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-center">
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                disabled={compressing}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-300 text-sm font-medium text-gray-700 hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
              >
                <FaImage className="text-base" />
                Dari File / Galeri
              </button>
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={compressing}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <FaCamera className="text-base" />
                Buka Kamera
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Foto otomatis dikompres sebelum diupload
            </p>
          </div>
        )}

        {compressing && (
          <div className="mt-3 flex items-center justify-center gap-2 text-sm text-primary">
            <FaSpinner className="animate-spin" />
            Mengompres gambar...
          </div>
        )}

        {error && (
          <p className="mt-2 text-sm text-red-500 text-center">{error}</p>
        )}
      </div>
    </div>
  );
}

export default ImageUpload;
